# Custom Questionnaire Panel with GraphQL

The SEEK [Questionnaire Panel] is built on operations available through our GraphQL endpoint.
You can programmatically retrieve recommended questions, define custom questions and privacy policies, and create questionnaires.
This allows you to build your own custom user interface for questionnaires.

## Before you begin

- Evaluate whether you can embed the SEEK-built [Questionnaire Panel], which is a simpler alternative to creating your own implementation.

- SEEK recommends using a [browser token] to manage questionnaires directly from a hirer’s browser.
  For the browser token to work, you will need to include the `mutate:application-questionnaires query:application-library-question-suggestions query:application-questionnaires` scope in your request.

- You can also manage questionnaires from your backend using a [partner token].

[browser token]: /auth/browser-tokens
[partner token]: /auth/partner-tokens

## Recommended questions

The [`applicationLibraryQuestionSuggestions`] query returns a list of suggested SEEK library questions based on a partial [`PositionProfile`].
This can be used to interactively suggest application questions to a hirer while they’re posting a job ad.

```mermaid question suggestions flow
sequenceDiagram
  participant H as Hirer
  participant P as Partner
  participant G as graphql.seek.com

  H->>P: Fill in job category, location and position title
  P->>G: Map position profile to SEEK structure
  G-->>P: Library question suggestions
  P->>H: Show library question suggestions
```

You should invoke the query whenever a hirer updates one of these fields:

- [Job category](/use-cases/job-posting/job-categories)

- [Location](/use-cases/job-posting/locations)

- [Position title](/schema/named-type/PostPosition_PositionProfileInput#/field/positionTitle)

While only the position’s title, category and location are required, providing [position descriptions] will increase suggestion relevance.

{/* We LOVATO ignore this as the suggestion IDs differ with each request. */}

```graphql
query (
  $positionProfile: ApplicationLibraryQuestionSuggestions_PositionProfileInput!
  $schemeId: String!
  $first: Int
) {
  applicationLibraryQuestionSuggestions(
    schemeId: $schemeId
    positionProfile: $positionProfile
    first: $first
  ) {
    id {
      value
    }
    applicationLibraryQuestion {
      text
      id {
        value
      }
      preferenceSelection {
        typeCode
        message
      }
      responseTypeCode
      responseChoice {
        id {
          value
        }
        text
      }
    }
  }
}
```

```json
{
  "schemeId": "seekAnzPublicTest",
  "first": 3,
  "positionProfile": {
    "jobCategories": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
    "positionFormattedDescriptions": {
      "descriptionId": "AdvertisementDetails",
      "content": "A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area."
    },
    "positionLocation": "seekAnzPublicTest:location:seek:2dkY4vZJF",
    "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
    "positionTitle": "Software developer"
  }
}
```

```json lovato-ignore-runnable-graphql-operation
{
  "applicationLibraryQuestionSuggestions": [
    {
      "id": {
        "value": "seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:7iRhoQSue6qBbKjiZUJMram9ssWJeVGNvC6PEF"
      },
      "applicationLibraryQuestion": {
        "text": "Which of the following statements best describes your right to work in Australia?",
        "id": {
          "value": "seekAnzPublicTest:applicationLibraryQuestion:seek:DKVufjvcySfpEnyu"
        },
        "preferenceSelection": {
          "typeCode": "MultiChoice",
          "message": "I will accept any of these answers"
        },
        "responseTypeCode": "SingleSelect",
        "responseChoice": [
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHdy"
            },
            "text": "I'm an Australian citizen"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHdz"
            },
            "text": "I'm a permanent resident and/or NZ citizen"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHe1"
            },
            "text": "I have a family/partner visa with no restrictions"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHe2"
            },
            "text": "I have a graduate temporary work visa"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHe3"
            },
            "text": "I have a holiday temporary work visa"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHe4"
            },
            "text": "I have a temporary visa with restrictions on work location (e.g. skilled regional visa 491)"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHe5"
            },
            "text": "I have a temporary protection or safe haven enterprise work visa"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gzU545v"
            },
            "text": "I have a temporary visa with no restrictions (e.g. doctoral student)"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHe6"
            },
            "text": "I have a temporary visa with restrictions on work hours (e.g. student visa, retirement visa)"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gzVXN7L"
            },
            "text": "I have a temporary visa with restrictions on industry (e.g. temporary activity visa 408)"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRF4sWNgVnsLCiVV455gspdHe7"
            },
            "text": "I require sponsorship to work for a new employer (e.g. 482, 457)"
          }
        ]
      }
    },
    {
      "id": {
        "value": "seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:3f3joEWTkKD4TUX4mBVLShPKzvrNkK8dNLvToDZUK"
      },
      "applicationLibraryQuestion": {
        "text": "How many years' experience do you have as a software developer?",
        "id": {
          "value": "seekAnzPublicTest:applicationLibraryQuestion:seek:5sdzYR5cDk78rrrzoLP"
        },
        "preferenceSelection": {
          "typeCode": "SingleChoice",
          "message": "I will accept above and including"
        },
        "responseTypeCode": "SingleSelect",
        "responseChoice": [
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7fkAJGKP9cUPsc8LurJNB"
            },
            "text": "No experience"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7fkAJGKP9cUPsc8LurJNC"
            },
            "text": "Less than 1 year"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7fkAJGKP9cUPsc8LurJST"
            },
            "text": "1 year"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7fkAJGKP9cUPsc8LurJSU"
            },
            "text": "2 years"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7fkAJGKP9cUPsc8LurJSV"
            },
            "text": "3 years"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7fkAJGKP9cUPsc8LurJSW"
            },
            "text": "4 years"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7fkAJGKP9cUPsc8LurJSX"
            },
            "text": "5 years"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7fkAJGKP9cUPsc8LurJSY"
            },
            "text": "More than 5 years"
          }
        ]
      }
    },
    {
      "id": {
        "value": "seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:ZGZYCeH57DyhHNa7RF78NP5eY5gd4C4j5nd7Zaf"
      },
      "applicationLibraryQuestion": {
        "text": "Which of the following programming languages are you experienced in?",
        "id": {
          "value": "seekAnzPublicTest:applicationLibraryQuestion:seek:22Cqsvc9FzhkTc748J"
        },
        "preferenceSelection": {
          "typeCode": "MultiChoice",
          "message": "I will only accept this combination of answers"
        },
        "responseTypeCode": "MultiSelect",
        "responseChoice": [
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqKQQ"
            },
            "text": "JavaScript"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqKQR"
            },
            "text": "HTML"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqKQS"
            },
            "text": "CSS"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqKQT"
            },
            "text": "Java"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqKQU"
            },
            "text": "C"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCX"
            },
            "text": "C#"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCY"
            },
            "text": "Python"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCZ"
            },
            "text": "C++"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCa"
            },
            "text": ".NET"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCb"
            },
            "text": "Objective-C"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCc"
            },
            "text": "PHP"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCd"
            },
            "text": "Swift"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCe"
            },
            "text": "Ruby"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCf"
            },
            "text": "Scala"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeCg"
            },
            "text": "Visual Basic"
          },
          {
            "id": {
              "value": "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:2FEwk3Pi7ZGSXEKPRdPLo93DXqeGw"
            },
            "text": "None of these"
          }
        ]
      }
    }
  ]
}
```

Each suggested [`applicationLibraryQuestion`] contains the details of a question from SEEK’s library.
A hirer may select up to 8 suggested questions for inclusion in their questionnaire.

You should present the hirer with an appropriate input control to record their preferred response choices to each selected question,
which drives [scoring] of questionnaire submissions.
The [`preferenceSelection`] field describes how you should present the response choice selection.

- The available [`typeCode`]s and corresponding sample implementations are listed below.

- The [`message`] is a description that should be displayed to the hirer to prompt their preference selection.

The following samples provide a visual representation of how you should present the hirer view for each scenario.
The accompanying candidate view approximates how the question is presented on SEEK’s Apply Form per the [`responseTypeCode`];
it exists for illustrative purposes only and does not need to be shown within your software.

import {
  SingleChoiceSingleSelectionQuestion,
  MultiChoiceSingleSelectionQuestion,
  MultiChoiceMultiSelectionQuestion,
  RangeSingleSelectionQuestion
} from './recommended-questions';

### Single choice

`SingleChoice` indicates that the hirer can select a single preferred response choice.
A radio group or dropdown should be used.

For a question with a `SingleSelect` candidate response:

<SingleChoiceSingleSelectionQuestion />

### Multi choice

`MultiChoice` indicates that the hirer can select multiple preferred response choices.
A checkbox group should be used.

For a question with a `SingleSelect` candidate response:

<MultiChoiceSingleSelectionQuestion />

For a question with a `MultiSelect` candidate response:

<MultiChoiceMultiSelectionQuestion />

### Range

`Range` indicates that the hirer can select two response choices representing the minimum and maximum preferred choices.
A set of two dropdowns should be used.

For a question with a `SingleSelect` candidate response:

<RangeSingleSelectionQuestion />

The minimum dropdown should implement the following rules:

1. The last option should be disabled

The maximum dropdown should implement the following rules:

1. The first option should be disabled
2. The selected minimum and preceding options should be disabled
3. The selection should be reset if the minimum is greater than or equal to it

## Custom questions

If a hirer is unable to find a relevant question from SEEK’s library,
they may wish to provide their own question.
A hirer may create up to 3 custom questions for inclusion in their questionnaire.

We recommend supporting the same question types as the SEEK-built [Questionnaire Panel]:

1. Free text
2. Yes/no
3. Single select
4. Multi select

However, you may choose to omit complex selection types if there is limited demand from your hirers.

import { CustomQuestion } from './custom-question';

<CustomQuestion />

The above sample generates the corresponding [`ApplicationQuestionnaireComponentInput`] to supply to [questionnaire creation](#questionnaire-creation).

## Privacy policy

If your software stores a link to the hirer’s privacy policy,
you can present a checkbox that requires candidates to accept the privacy policy as part of the SEEK’s Apply Form.
The description and URL of the privacy policy should be populated by your backend,
as the hirer shouldn’t need to tailor these details per job ad.

import { PrivacyPolicy } from './privacy-policy';

<PrivacyPolicy />

The above sample generates the corresponding [`ApplicationQuestionnaireComponentInput`] to supply to [questionnaire creation](#questionnaire-creation).

## Questionnaire creation

The [`createApplicationQuestionnaire`] mutation creates a new questionnaire.
You can include SEEK library questions from the [`applicationLibraryQuestionSuggestions`] query, custom questions, and a privacy consent component.

### For recommended questions

If a library question was suggested via the [`applicationLibraryQuestionSuggestions`] query,
you should supply its [`ApplicationLibraryQuestionSuggestion.id`] as input to the [`createApplicationQuestionnaire`] mutation.

```graphql
mutation ($input: CreateApplicationQuestionnaireInput!) {
  createApplicationQuestionnaire(input: $input) {
    applicationQuestionnaire {
      id {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "applicationQuestionnaire": {
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",
      "components": [
        {
          "componentTypeCode": "Question",
          "libraryQuestion": {
            "componentTypeCode": "Question",
            "id": "seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:2PPe5xSH2Sw1bQR1HKKzx9McxyvsGjSWQW6t7",
            "selectedResponseChoice": [
              "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:4LqV9pD5uNXwgUe42maXd8NBeo"
            ]
          }
        },
        {
          "componentTypeCode": "Question",
          "libraryQuestion": {
            "componentTypeCode": "Question",
            "id": "seekAnzPublicTest:applicationLibraryQuestion:seek:3YVmYzzxFKZ4ert",
            "selectedResponseChoice": [
              "seekAnzPublicTest:applicationLibraryQuestionChoice:seek:4LqV9pD5uNXwgUe42maXd8NBeo"
            ]
          }
        }
      ]
    }
  }
}
```

### For custom questions and privacy policies

```graphql
mutation ($input: CreateApplicationQuestionnaireInput!) {
  createApplicationQuestionnaire(input: $input) {
    applicationQuestionnaire {
      id {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "applicationQuestionnaire": {
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",
      "components": [
        {
          "componentTypeCode": "Question",
          "question": {
            "componentTypeCode": "Question",
            "value": "question1",
            "questionHtml": "Tell us about your relevant mock data experience",
            "responseTypeCode": "FreeText"
          }
        },
        {
          "componentTypeCode": "PrivacyConsent",
          "privacyConsent": {
            "componentTypeCode": "PrivacyConsent",
            "value": "consent1",
            "descriptionHtml": "Do you agree to our privacy policy?",
            "privacyPolicyUrl": {
              "url": "https://au.seek.com/privacy"
            }
          }
        }
      ]
    }
  }
}
```

## Questionnaire retrieval

The [`applicationQuestionnaire` query] retrieves a previously created questionnaire by its identifier.

```graphql
query ($id: String!) {
  applicationQuestionnaire(id: $id) {
    id {
      value
    }
    components {
      id {
        value
      }
      componentTypeCode
      ... on ApplicationQuestion {
        questionHtml
        responseTypeCode
        value
        responseChoice {
          text
          value
        }
      }
      ... on ApplicationPrivacyConsent {
        descriptionHtml
        privacyPolicyUrl {
          url
        }
      }
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr"
}
```

```json
{
  "applicationQuestionnaire": {
    "id": {
      "value": "seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr"
    },
    "components": [
      {
        "id": {
          "value": "seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCe8dro9Sw"
        },
        "componentTypeCode": "Question",
        "questionHtml": "Which of the following statements best describes your right to work in Australia?",
        "responseTypeCode": "SingleSelect",
        "value": null,
        "responseChoice": [
          {
            "text": "I'm an Australian citizen",
            "value": null
          },
          {
            "text": "I'm a permanent resident and/or NZ citizen",
            "value": null
          },
          {
            "text": "I have a family/partner visa with no restrictions",
            "value": null
          },
          {
            "text": "I have a graduate temporary work visa",
            "value": null
          },
          {
            "text": "I have a holiday temporary work visa",
            "value": null
          },
          {
            "text": "I have a temporary visa with restrictions on work location (e.g. skilled regional visa 489)",
            "value": null
          },
          {
            "text": "I have a temporary protection or safe haven enterprise work visa",
            "value": null
          },
          {
            "text": "I have a temporary visa with no restrictions (e.g. doctoral student)",
            "value": null
          },
          {
            "text": "I have a temporary visa with restrictions on work hours (e.g. student visa, retirement visa",
            "value": null
          },
          {
            "text": "I require sponsorship to work for a new employer (e.g. 482, 457)",
            "value": null
          }
        ]
      },
      {
        "id": {
          "value": "seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCedX2sdyZ"
        },
        "componentTypeCode": "Question",
        "questionHtml": "Do you own or have regular access to a car?",
        "responseTypeCode": "SingleSelect",
        "value": null,
        "responseChoice": [
          {
            "text": "Yes",
            "value": null
          },
          {
            "text": "No",
            "value": null
          }
        ]
      },
      {
        "id": {
          "value": "seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCf8QCx8WB"
        },
        "componentTypeCode": "Question",
        "questionHtml": "What do you like most about GraphQL?",
        "responseTypeCode": "FreeText",
        "value": null,
        "responseChoice": []
      },
      {
        "id": {
          "value": "seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCfdHP2d2o"
        },
        "componentTypeCode": "PrivacyConsent",
        "descriptionHtml": "Do you agree with our privacy policy?",
        "privacyPolicyUrl": {
          "url": "https://au.seek.com/privacy"
        }
      }
    ]
  }
}
```

[`applicationlibraryquestion`]: /schema/named-type/ApplicationLibraryQuestion
[`applicationlibraryquestionsuggestion.id`]: /schema/named-type/ApplicationLibraryQuestionSuggestion#/field/id
[`applicationlibraryquestionsuggestions`]: /schema/query/applicationLibraryQuestionSuggestions
[`applicationquestionnaire` query]: /schema/query/applicationQuestionnaire
[`applicationquestionnairecomponentinput`]: /schema/named-type/ApplicationQuestionnaireComponentInput
[`createapplicationquestionnaire`]: /schema/mutation/createApplicationQuestionnaire
[`message`]: /schema/named-type/ApplicationLibraryQuestionPreferenceSelection#/field/message
[`positionprofile`]: /schema/named-type/PositionProfile
[`preferenceselection`]: /schema/named-type/ApplicationLibraryQuestion#/field/preferenceSelection
[position descriptions]: /schema/named-type/PositionFormattedDescriptionInput
[`responsetypecode`]: /schema/named-type/ApplicationLibraryQuestion#/field/responseTypeCode
[`typecode`]: /schema/named-type/ApplicationLibraryQuestionPreferenceSelection#/field/typeCode
[questionnaire panel]: /use-cases/job-posting/questionnaires/v2/panel
[scoring]: /use-cases/job-posting/questionnaires#preferred-choices--scoring
